import type { FC } from "react"
import { useState } from "react"
import { useNavigate, useParams } from "@tanstack/react-router"
import { IconPlusOutline18 as Plus } from "nucleo-ui-outline-18"

import { getHostname } from "@productlane/lib/src/browser"
import { Button, Empty, Tooltip } from "@productlane/ui"

import { MetaInfo } from "@/components/meta-info"
import { useSetBackNavigation } from "@/lib/back-navigation"
import { hasLinearConnection } from "@/lib/linear-connection"
import { useZeroWorkspace } from "@/lib/zero"
import { SupportPortalSettingsContent } from "../../settings/support-portal-settings-content"
import { CreateCustomerDialog } from "../customers/create-customer-dialog"
import { CustomersDetailScreen } from "../customers/detail-screen"
import { PageHeader } from "../page-header"
import { PublishPortalPageButton } from "../portal/publish-portal-page-button"
import { ContactPortalPreviewScreen } from "./contact-preview-screen"
import { CrosshairsIcon } from "./crosshairs-icon"
import { SupportPortalNav } from "./support-portal-nav"

const SUPPORT_PORTAL_ARTICLE = "7e861235-26a4-4b00-9268-a0e82258bbe6"

export const SupportPortalScreen: FC = () => {
	const params = useParams({ strict: false })
	const companyId = params.companyId
	const contactId = params.contactId
	useSetBackNavigation({ type: "support-portal", url: "/support-portal" })

	return (
		<>
			<MetaInfo title="Support portal" />
			<div className="flex min-h-0 w-full flex-1 overflow-hidden">
				<SupportPortalNav
					selectedCompanyId={companyId}
					selectedContactId={contactId}
				/>
				<div className="flex flex-shrink flex-grow flex-col overflow-hidden">
					{contactId ? (
						<ContactPortalPreviewScreen key={contactId} contactId={contactId} />
					) : companyId ? (
						<CustomersDetailScreen
							key={companyId}
							companyId={companyId}
							parentLabel="Support portal"
							parentPath="/support-portal"
							isSupportPortalPreview
						/>
					) : (
						<CompaniesOverview />
					)}
				</div>
			</div>
		</>
	)
}

const CompaniesOverview = () => {
	const workspace = useZeroWorkspace()
	const navigate = useNavigate()
	const [isCreateOpen, setIsCreateOpen] = useState(false)

	const openDocs = () => {
		// @ts-expect-error Global widget call
		// eslint-disable-next-line
		window.Productlane.openDocs(SUPPORT_PORTAL_ARTICLE)
	}

	return (
		<>
			<PageHeader
				title="Overview"
				titleClassName="text-sm font-medium"
				className="pl-12 pr-3 sm:pl-2 lg:pl-[17.5px]"
				actions={
					workspace ? (
						<PublishPortalPageButton
							content={<SupportPortalSettingsContent />}
							isPublished={workspace.customerPortalSettings?.isEnabled || false}
							url={`https://${workspace.domain === "hello" ? "productlane.com" : getHostname(workspace)}/support`}
							name="support-portal"
						/>
					) : undefined
				}
			/>
			<div className="flex flex-1 items-center justify-center overflow-y-auto p-6">
				<div className="w-full max-w-md">
					<Empty
						variant="left"
						title="Support portal preview"
						text="Preview what each company sees when their team logs into your support portal. Pick a company from the sidebar to see their view."
						image={<CrosshairsIcon />}
						action={
							<div className="flex items-center gap-2">
								<Tooltip
									label="Connect Linear to create a company"
									enabled={!hasLinearConnection(workspace)}
									shouldWrapChildren
								>
									<Button
										Icon={Plus}
										disabled={!hasLinearConnection(workspace)}
										onClick={() => setIsCreateOpen(true)}
									>
										Create company
									</Button>
								</Tooltip>
								<Button variant="outline" onClick={openDocs}>
									Documentation
								</Button>
							</div>
						}
					/>
				</div>
			</div>
			<CreateCustomerDialog
				isOpen={isCreateOpen}
				onOpenChange={(value, created) => {
					setIsCreateOpen(value)
					if (created) {
						void navigate({
							to: "/support-portal/$companyId",
							params: { companyId: created.id },
						})
					}
				}}
			/>
		</>
	)
}
